<template>
  <div>
    <v-container       
      class="full-height"
      fluid
    >
      <v-card>
        <v-card-title>Head Count Report</v-card-title>
        <v-row class="px-4">
          <v-col
            cols="8"
            class="pt-0"
          >
            <v-data-table
              v-model="selectedEmployees"
              v-validate="'required'"
              dense
              :error-messages="errors.collect('selectedEmployees')"
              data-vv-name="selectedEmployees"
              data-vv-as="employees"
              :headers="headers"
              :items="filteredEmployees"
              hide-default-footer
              fixed-header
              disable-pagination
              show-select
              item-key="employee_uid"
              item-value="employee_uid"
              class="elevation-2 mb-3"
              height="420px"
            >
              <template v-slot:top>
                <h5 class="font-weight-bold py-2 mb-0 pl-2">
                  Employee Selection:
                </h5>
              </template> 
              <template v-slot:item.ic_number="{ item }">
                {{ ((item.person || {}).identity_card || {}).ic_number ? ((item.person || {}).identity_card || {}).ic_number : ((item.person || {}).identity_card || {}).passport_number }}
              </template> 
            </v-data-table>
            <span class="red--text text--lighten-1 caption">{{ errors.first('selectedEmployees') }}</span>

            <v-row class="pl-4">
              <v-col>
                <span>Employee Status</span>
                <v-radio-group
                  v-model="isActive"
                  class="mt-0"
                  row
                >
                  <v-radio
                    label="Active"
                    :value="1"
                  />
                  <v-radio
                    label="Inactive"
                    :value="0"
                  /> 
                  <v-radio
                    label="All"
                    :value="2"
                  />
                </v-radio-group>
              </v-col>

              <v-col>
                <span>Include Employee Details?</span>
                <v-radio-group
                  v-model="isIncludeEmployeeDetails"
                  class="mt-0"
                  row
                >
                  <v-radio
                    label="Yes"
                    :value="1"
                  />
                  <v-radio
                    label="No"
                    :value="0"
                  /> 
                </v-radio-group>
              </v-col> 
            </v-row>                     
          </v-col>

          <v-col
            cols="4"
            class="pt-0"
          >
            <v-text-field
              v-model="searchInput"
              placeholder="Filter employee by name"
              append-icon="search"
              label="Search Employee"
              hide-no-data
              hide-selected
            />

            <v-text-field
              v-model="searchIC"
              placeholder="Filter employee by IC"
              append-icon="search"
              label="Search Employee IC"
              hide-no-data
              hide-selected
            />
            
            <v-autocomplete
              v-model="filters.companies"
              :items="companies"
              item-text="name"
              item-value="id"
              label="Company"
              placeholder="Filter employee by company"
              clearable
              hide-selected
              multiple
              small-chips
              deletable-chips
              chips
            />

            <v-autocomplete
              v-model="filters.positions"
              :items="positions"
              item-text="label"
              item-value="id"
              value="id"
              label="Position"
              placeholder="Filter employee by position"
              clearable
              hide-selected
              multiple
              small-chips
              deletable-chips
              chips
            />

            <v-autocomplete
              v-model="filters.departments"
              :items="departments"
              item-text="name"
              item-value="id"
              value="id"
              label="Department"
              placeholder="Filter employee by department"
              clearable
              hide-selected
              multiple
              deletable-chips
              small-chips
              chips
            />

            <v-autocomplete
              v-model="filters.costCenters"
              :items="filteredCostCentersByCompany"
              item-text="label"
              item-value="id"
              label="Cost Center"
              placeholder="Filter employee by cost center"
              clearable
              hide-selected
              multiple
              small-chips
              deletable-chips
              chips
            />

            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="startDateDisplay"
                  v-validate="'required'"
                  label="Start Date"
                  prepend-icon="mdi-calendar"
                  :error-messages="errors.collect('startDateDisplay')"
                  data-vv-name="startDateDisplay"  
                  data-vv-as="start date"    
                  readonly
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="startDate"
                :max="endDate"
                @input="startDateMenu = false"
              />
            </v-menu>

            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="endDateDisplay"
                  v-validate="'required'"
                  label="End Date"
                  prepend-icon="mdi-calendar"
                  :error-messages="errors.collect('endDateDisplay')"
                  data-vv-name="endDateDisplay"  
                  data-vv-as="end date"  
                  readonly
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="endDate"
                :min="startDate"
                @input="endDateMenu = false"
              />
            </v-menu>

            <v-card-actions>
              <v-spacer />
              <v-btn
                block
                :loading="progressing"
                color="hrissecondary"     
                class="white--text"     
                @click="generate"
              >
                Generate
              </v-btn>
            </v-card-actions>
          </v-col>
        </v-row>  
      </v-card>
    </v-container>  
  </div>      
</template>
<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      employees: {
        type: Array,
        required: true,
      },
      positions: {
        type: Array,
        required: true,
      },
      departments: {
        type: Array,
        required: true,
      },
      companies: {
        type: Array,
        required: true,
      },
      costCenters: {
        type: Array,
        default: () => [],
      },
    },
    data () {
      return {
        progressing: false,
        searchInput: '',
        searchIC: '',
        filters: {
          companies: '',
          positions: '',
          departments: '',
          costCenters: '',
        },
        selectedCompanies: [],
        selectedEmployees: [],
        headers: [
          {
            text: 'Name',
            align: 'left',
            sortable: true,
            value: 'person.name',
          },
          {
            text: 'IC',
            align: 'left',
            sortable: true,
            value: 'ic_number',
          },
          {
            text: 'Department',
            align: 'left',
            sortable: true,
            value: 'department.name',
          },
          {
            text: 'Position',
            align: 'left',
            sortable: true,
            value: 'current_position.position.name',
          }, 
          {
            text: 'Resigned Date',
            align: 'left',
            sortable: true,
            value: 'end_date',
          },       
        ],
        isActive: 1,
        isIncludeEmployeeDetails: 0,
        startDateMenu: false,
        startDate: this.$moment().startOf('year').format('YYYY-MM-DD'),
        endDateMenu: false,
        endDate: this.$moment().format('YYYY-MM-DD'),
      }
    },
    computed: {
      startDateDisplay () {
        return this.startDate ? this.$moment(this.startDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      endDateDisplay () {
        return this.endDate ? this.$moment(this.endDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      filteredCostCentersByCompany () {
        const selectedCompanies = this.filters.companies || []

        if (!selectedCompanies.length) return this.costCenters

        return this.costCenters.filter(cc => selectedCompanies.includes(cc.selected_company_uid))
      },
      filteredEmployees () {
        let _this = this
        _this.searchInput = _this.searchInput.trim()
        let primaryFilterEmployees = this.employees.filter(function (employee) {
          return ((employee.person || {}).name || '').toLowerCase().indexOf(_this.searchInput.toLowerCase()) !== -1 
        })

        _this.searchIC = _this.searchIC.trim()
        let secondaryFilterEmployees = primaryFilterEmployees.filter(function (employee) {
          return ((employee.person.identity_card || {}).ic_number || '').toLowerCase().indexOf(_this.searchIC.toLowerCase()) !== -1 ||
            ((employee.person.identity_card || {}).passport_number || '').toLowerCase().indexOf(_this.searchIC.toLowerCase()) !== -1
        })
        
        let tertiaryFilterEmployees = secondaryFilterEmployees.filter(function (employee) {
          if (_this.isActive === 1) {
            return (!employee.end_date || employee.end_date > _this.endDate)
          } else if (_this.isActive === 0) {
            return (employee.end_date && employee.end_date >= _this.startDate && employee.end_date <= _this.endDate)
          } else {
            return ((!employee.end_date || employee.end_date > _this.endDate) || (employee.end_date && employee.end_date >= _this.startDate && employee.end_date <= _this.endDate))
          }
        })

        let filteredCompanies = this.customFilterCompanies(tertiaryFilterEmployees)
        let filteredPositions = this.customFilterPositions(filteredCompanies)
        let filteredDepartments = this.customFilterDepartments(filteredPositions)
        let filteredCostCenters = this.customFilterCostCenters(filteredDepartments)
        return filteredCostCenters
      },
    },
    watch: {
      filteredEmployees (newList) {
        this.$validator.pause()

        const newEmployeeUids = new Set(newList.map(emp => emp.employee_uid))

        this.selectedEmployees = this.selectedEmployees.filter(selectedEmp =>
          newEmployeeUids.has(selectedEmp.employee_uid)
        )

        this.$nextTick(() => {
          this.$validator.resume()
        })
      },
    },
    methods: {
      prepareFormData () {
        return {
          employeeUids: this.getEmployeeUids(),
          start_date: this.startDate,
          end_date: this.endDate,
          mergeEmployeeDetails: this.isIncludeEmployeeDetails,
          costCenterIds: this.filters.costCenters,
        }
      },
      generate () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            this.$api.employeeReports().headCountReport(this.prepareFormData())
              .then(res => {
                let blob = new Blob([res.data], { type: res.data.type })
                let link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.setAttribute('download', 'Employee Headcount Report.xlsx')
                link.click()

                this.progressing = false
                this.$snack({ message: 'Report downloaded successfully' })
              })
              .catch(() => {
                this.progressing = false
                this.$snack({
                  color: 'error',
                  message: 'Failed to generate report. Please contact IT support',
                })
              })
          })
      },

      getEmployeeUids () {
        let employeeUids = []

        this.selectedEmployees.map(item => {
          employeeUids.push(item.employee_uid)
        })

        return employeeUids
      },

      customFilterCompanies (employeesList) {
        if (this.filters.companies.length === 0) return employeesList

        return employeesList.filter(employee => {
          if (employee.company_employees.length <= 0) return 

          let companyEmployees = employee.company_employees.filter(companyEmployee => {
            if (this.isActive === 1) {
              return companyEmployee.is_active_pay_status === 1 && companyEmployee.start_date <= this.endDate && (!companyEmployee.end_date || companyEmployee.end_date >= this.startDate)
            } else if (this.isActive === 0) {
              return (companyEmployee.end_date && companyEmployee.end_date >= this.startDate && companyEmployee.end_date <= this.endDate)
            } else {
              return (
                companyEmployee.is_active_pay_status === 1 && companyEmployee.start_date <= this.endDate && 
                (!companyEmployee.end_date || companyEmployee.end_date >= this.startDate)
              ) || (companyEmployee.end_date && companyEmployee.end_date >= this.startDate && companyEmployee.end_date <= this.endDate)
            }
          })

          if (companyEmployees.length > 0) {
            let latestPayrollCompany = companyEmployees[0].company_uid
  
            return _.intersection([latestPayrollCompany], this.filters.companies).length > 0
          }
        })
      },

      customFilterPositions (employeesList) {
        if (this.filters.positions.length === 0) return employeesList

        return employeesList.filter(employee => {
          let positionId = (employee.current_position || {}).position_id
  
          return _.intersection([positionId], this.filters.positions).length > 0
        })
      },

      customFilterDepartments (employeesList) {
        if (this.filters.departments.length === 0) return employeesList

        return employeesList.filter(employee => {
          return _.intersection([employee.department_id], this.filters.departments).length > 0
        })
      },

      customFilterCostCenters (employeesList) {
        if (this.filters.costCenters.length === 0) return employeesList

        const today = this.$moment().format('YYYY-MM-DD')
        const from = this.startDate || today
        const to = this.endDate || today

        return employeesList.filter(employee => {
          const costCenterIds = _.flatMap(employee.company_employees || [], companyEmployee => companyEmployee.company_employee_cost_centers || [])
            .filter(costCenter => (!costCenter.start_date || costCenter.start_date <= to) && (!costCenter.end_date || costCenter.end_date >= from))
            .map(costCenter => costCenter.cost_center_id)

          return _.intersection(costCenterIds, this.filters.costCenters).length > 0
        })
      },
    },
  }
</script>
